/**
 * Dual Calendar Component
 * 
 * Displays two months side by side with budget period highlighting.
 * Shows the current budget period dates with visual indicators.
 * Includes tooltips showing revenue/expense details on hover.
 */
import { useMemo, useState, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Calendar, Banknote, X } from 'lucide-react';
import { format, parseISO, startOfDay, endOfDay, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import type { BudgetPeriod } from '@/types/financial';
import 'react-day-picker/dist/style.css';

/** Ícones de navegação do react-day-picker. Ficam no escopo do módulo porque o
 *  `components` do DayPicker os RENDERIZA como componentes (`<IconLeft />`), e
 *  definir inline recria a identidade a cada render. Não recebem props nem
 *  capturam nada do escopo do componente pai. */
const IconeAnterior = () => <ChevronLeft className="h-4 w-4" />;
const IconeProximo = () => <ChevronRight className="h-4 w-4" />;

// Types for revenues and expenses with detailed info
interface RevenueInfo {
  name: string;
  expected_date?: string;
  status: string;
  expected_amount?: number;
  realized_amount?: number;
}

interface ExpenseInfo {
  name: string;
  due_date?: string;
  status: string;
  expected_amount?: number;
  realized_amount?: number;
}

interface DualCalendarProps {
  currentPeriod?: BudgetPeriod;
  selectedDate?: Date;
  onSelectDate?: (date: Date | undefined) => void;
  className?: string;
  revenues?: RevenueInfo[];
  expenses?: ExpenseInfo[];
}

// Format currency for tooltip display
const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

// Get status badge styling
const getStatusBadge = (status: string) => {
  const statusLower = status.toLowerCase();
  switch (statusLower) {
    case 'pending':
      return { label: 'Pendente', className: 'bg-amber-500/20 text-amber-600 dark:text-amber-400' };
    case 'partially_realized':
      return { label: 'Parcial', className: 'bg-blue-500/20 text-blue-600 dark:text-blue-400' };
    case 'realized':
      return { label: 'Realizado', className: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' };
    default:
      return { label: status, className: 'bg-gray-500/20 text-gray-600 dark:text-gray-400' };
  }
};

// Tooltip Popup Component
interface TooltipPopupProps {
  date: Date;
  revenues: RevenueInfo[];
  expenses: ExpenseInfo[];
  position: { x: number; y: number };
  onClose: () => void;
}

function TooltipPopup({ date, revenues, expenses, position, onClose }: Readonly<TooltipPopupProps>) {
  const dayRevenues = revenues.filter(r => {
    if (!r.expected_date) return false;
    try {
      const revenueDate = parseISO(r.expected_date);
      const statusLower = (r.status || '').toLowerCase();
      return isSameDay(revenueDate, date) && 
             (statusLower === 'pending' || statusLower === 'partially_realized');
    } catch {
      return false;
    }
  });

  const dayExpenses = expenses.filter(e => {
    if (!e.due_date) return false;
    try {
      const expenseDate = parseISO(e.due_date);
      const statusLower = (e.status || '').toLowerCase();
      return isSameDay(expenseDate, date) && 
             (statusLower === 'pending' || statusLower === 'partially_realized');
    } catch {
      return false;
    }
  });

  const totalRevenues = dayRevenues.reduce((sum, r) => sum + (Number(r.expected_amount) || 0), 0);
  const totalExpenses = dayExpenses.reduce((sum, e) => sum + (Number(e.expected_amount) || 0), 0);

  return (
    <div 
      className="fixed z-[100] w-80 bg-popover/98 backdrop-blur-md border border-border/60 rounded-xl shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
      style={{ 
        top: Math.max(10, position.y - 200), 
        left: Math.min(window.innerWidth - 340, Math.max(10, position.x - 160))
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Tooltip Header */}
      <div className="px-4 py-3 border-b border-border/40 bg-gradient-to-r from-primary/10 to-transparent rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/20 rounded-lg">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <span className="font-semibold text-sm">
              {format(date, "dd 'de' MMMM, yyyy", { locale: ptBR })}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-6 w-6 hover:bg-destructive/10"
            onClick={onClose}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Tooltip Content */}
      <div className="p-4 space-y-4 max-h-72 overflow-y-auto">
        {/* Revenues Section */}
        {dayRevenues.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              <div className="p-1 bg-emerald-500/20 rounded">
                <TrendingUp className="h-3.5 w-3.5" />
              </div>
              <span>Receitas Previstas</span>
              <span className="ml-auto text-sm">{formatCurrency(totalRevenues)}</span>
            </div>
            <div className="space-y-2">
              {dayRevenues.map((revenue, idx) => {
                const statusBadge = getStatusBadge(revenue.status);
                return (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-colors"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-foreground">
                        {revenue.name}
                      </span>
                      <span className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full w-fit font-medium",
                        statusBadge.className
                      )}>
                        {statusBadge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-emerald-500" />
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(revenue.expected_amount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Divider if both exist */}
        {dayRevenues.length > 0 && dayExpenses.length > 0 && (
          <div className="border-t border-border/40" />
        )}

        {/* Expenses Section */}
        {dayExpenses.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-rose-600 dark:text-rose-400">
              <div className="p-1 bg-rose-500/20 rounded">
                <TrendingDown className="h-3.5 w-3.5" />
              </div>
              <span>Despesas a Vencer</span>
              <span className="ml-auto text-sm">{formatCurrency(totalExpenses)}</span>
            </div>
            <div className="space-y-2">
              {dayExpenses.map((expense, idx) => {
                const statusBadge = getStatusBadge(expense.status);
                return (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-rose-500/10 to-rose-500/5 border border-rose-500/20 hover:border-rose-500/40 transition-colors"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-foreground">
                        {expense.name}
                      </span>
                      <span className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full w-fit font-medium",
                        statusBadge.className
                      )}>
                        {statusBadge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-rose-500" />
                      <span className="text-sm font-bold text-rose-600 dark:text-rose-400">
                        {formatCurrency(expense.expected_amount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Tooltip Footer Summary */}
      {(totalRevenues > 0 || totalExpenses > 0) && (
        <div className="px-4 py-3 border-t border-border/40 bg-muted/30 rounded-b-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Saldo do dia</span>
            <span className={cn(
              "text-sm font-bold",
              totalRevenues - totalExpenses >= 0 
                ? "text-emerald-600 dark:text-emerald-400" 
                : "text-rose-600 dark:text-rose-400"
            )}>
              {totalRevenues - totalExpenses >= 0 ? '+' : ''}{formatCurrency(totalRevenues - totalExpenses)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function DualCalendar({
  currentPeriod,
  selectedDate,
  onSelectDate,
  className,
  revenues = [],
  expenses = [],
}: Readonly<DualCalendarProps>) {
  // State for tooltip
  const [tooltipData, setTooltipData] = useState<{
    date: Date;
    position: { x: number; y: number };
  } | null>(null);

  // Parse period dates
  const periodStart = currentPeriod?.start_date
    ? startOfDay(parseISO(currentPeriod.start_date))
    : undefined;
  const periodEnd = currentPeriod?.end_date
    ? endOfDay(parseISO(currentPeriod.end_date))
    : undefined;

  // Calculate dates with revenues (pending or partially_realized)
  const revenueDates = useMemo(() => {
    return revenues
      .filter((r) => r.expected_date && ['pending', 'partially_realized', 'PENDING', 'PARTIALLY_REALIZED'].includes(r.status))
      .map((r) => parseISO(r.expected_date!));
  }, [revenues]);

  // Calculate dates with expenses (pending or partially_realized)
  const expenseDates = useMemo(() => {
    return expenses
      .filter((e) => e.due_date && ['pending', 'partially_realized', 'PENDING', 'PARTIALLY_REALIZED'].includes(e.status))
      .map((e) => parseISO(e.due_date!));
  }, [expenses]);

  // Handle day selection for tooltip
  const handleDaySelect = useCallback((date: Date | undefined) => {
    if (!date) {
      onSelectDate?.(date);
      return;
    }
    
    // Check if date has items - simplified logic that checks any pending status
    const hasRevenue = revenues.some(r => {
      if (!r.expected_date) return false;
      try {
        const revenueDate = parseISO(r.expected_date);
        const statusLower = (r.status || '').toLowerCase();
        return isSameDay(revenueDate, date) && 
               (statusLower === 'pending' || statusLower === 'partially_realized');
      } catch {
        return false;
      }
    });
    
    const hasExpense = expenses.some(e => {
      if (!e.due_date) return false;
      try {
        const expenseDate = parseISO(e.due_date);
        const statusLower = (e.status || '').toLowerCase();
        return isSameDay(expenseDate, date) && 
               (statusLower === 'pending' || statusLower === 'partially_realized');
      } catch {
        return false;
      }
    });
    
    if (hasRevenue || hasExpense) {
      // Get the position of the calendar element for tooltip positioning
      const calendarElement = document.querySelector('.rdp');
      const rect = calendarElement?.getBoundingClientRect();
      setTooltipData({
        date,
        position: { 
          x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2, 
          y: rect ? rect.top + 100 : window.innerHeight / 2 
        }
      });
    } else {
      onSelectDate?.(date);
    }
  }, [revenues, expenses, onSelectDate]);

  // Close tooltip
  const closeTooltip = useCallback(() => {
    setTooltipData(null);
  }, []);

  // Calculate the month to show (start from period start or current date)
  const displayMonth = periodStart || new Date();

  // Date modifiers for styling
  const modifiers = useMemo(() => {
    const mods: Record<string, Date[]> = {
      periodStart: periodStart ? [periodStart] : [],
      periodEnd: periodEnd ? [periodEnd] : [],
      hasRevenue: revenueDates,
      hasExpense: expenseDates,
    };

    // Add period range days
    if (periodStart && periodEnd) {
      const periodDays: Date[] = [];
      const currentDate = new Date(periodStart);
      while (currentDate <= periodEnd) {
        periodDays.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      mods.inPeriod = periodDays;
    }

    return mods;
  }, [periodStart, periodEnd, revenueDates, expenseDates]);

  // Disabled dates function - only dates within budget period are selectable
  const disabledDates = useMemo(() => {
    if (!periodStart || !periodEnd) return undefined
    
    return (date: Date) => {
      const d = startOfDay(date)
      return d < periodStart || d > periodEnd
    }
  }, [periodStart, periodEnd]);

  // Custom modifier class names for better styling control
  const modifiersClassNames = {
    inPeriod: 'bg-primary/10',
    periodStart: 'bg-primary text-foreground rounded-l-full font-bold',
    periodEnd: 'bg-primary text-foreground rounded-r-full font-bold',
    hasRevenue: 'calendar-has-revenue',
    hasExpense: 'calendar-has-expense',
  };

  return (
    <div className={cn('p-4 bg-card rounded-xl border border-border relative', className)}>
      {/* Period Info Header */}
      {currentPeriod && (
        <div className="mb-4 pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-sm text-foreground">
                Período Orçamentário
              </h3>
              <p className="text-xs text-muted-foreground">
                {periodStart && periodEnd && (
                  <>
                    {format(periodStart, "dd 'de' MMMM", { locale: ptBR })} até{' '}
                    {format(periodEnd, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />
                <span className="text-muted-foreground">Receitas</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-sm shadow-rose-500/50" />
                <span className="text-muted-foreground">Despesas</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instruction */}
      <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
        <span className="text-primary">💡</span>
        Clique nas datas com indicadores para ver detalhes
      </p>

      {/* Custom styles for calendar indicators */}
      <style>{`
        .calendar-has-revenue {
          position: relative;
          cursor: pointer !important;
        }
        .calendar-has-revenue::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #10b981;
          box-shadow: 0 0 6px rgba(16, 185, 129, 0.6);
          animation: pulse-green 2s infinite;
        }
        .calendar-has-expense {
          position: relative;
          cursor: pointer !important;
        }
        .calendar-has-expense::before {
          content: '';
          position: absolute;
          bottom: 2px;
          left: calc(50% + 6px);
          transform: translateX(-50%);
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #ef4444;
          box-shadow: 0 0 6px rgba(239, 68, 68, 0.6);
          animation: pulse-red 2s infinite;
        }
        .calendar-has-revenue.calendar-has-expense::after {
          left: calc(50% - 6px);
        }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 4px rgba(16, 185, 129, 0.4); }
          50% { box-shadow: 0 0 8px rgba(16, 185, 129, 0.8); }
        }
        @keyframes pulse-red {
          0%, 100% { box-shadow: 0 0 4px rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 8px rgba(239, 68, 68, 0.8); }
        }
      `}</style>

      {/* Calendar */}
      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={handleDaySelect}
        defaultMonth={displayMonth}
        numberOfMonths={2}
        locale={ptBR}
        showOutsideDays
        disabled={disabledDates}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        className="!p-0"
        classNames={{
          months: 'flex flex-col sm:flex-row gap-4',
          month: 'flex-1 space-y-4',
          caption: 'flex justify-center pt-1 relative items-center h-10',
          caption_label: 'text-sm font-semibold capitalize text-foreground',
          nav: 'flex items-center gap-1',
          nav_button: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-muted'
          ),
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse',
          head_row: 'flex',
          head_cell:
            'text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center',
          row: 'flex w-full mt-2',
          cell: cn(
            'flex-1 text-center text-sm p-0 relative',
            '[&:has([aria-selected])]:bg-accent',
            'first:[&:has([aria-selected])]:rounded-l-md',
            'last:[&:has([aria-selected])]:rounded-r-md',
            'focus-within:relative focus-within:z-20'
          ),
          day: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-9 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-muted/50'
          ),
          day_selected:
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
          day_today:
            'relative z-0 text-white font-bold after:content-[""] after:absolute after:left-1/2 after:top-1/2 after:h-6 after:w-6 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-emerald-700 dark:after:bg-emerald-600 after:z-[-1]',
          day_outside: 'text-muted-foreground opacity-50',
          day_disabled: 'text-muted-foreground opacity-50',
          day_hidden: 'invisible',
        }}
        components={{ IconLeft: IconeAnterior, IconRight: IconeProximo }}
      />

      {/* Footer with stats */}
      <div className="mt-4 pt-3 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
            {revenueDates.length} receitas pendentes
          </span>
          <span className="flex items-center gap-1.5">
            <TrendingDown className="h-3.5 w-3.5 text-rose-500" />
            {expenseDates.length} despesas pendentes
          </span>
        </div>
      </div>

      {/* Tooltip Popup */}
      {tooltipData && (
        <>
          {/* Backdrop — decorativo: só captura o clique fora para fechar.
              aria-hidden porque não há nada aqui para leitor de tela anunciar;
              quem usa teclado fecha pelo botão X do próprio tooltip. */}
          <div
            className="fixed inset-0 z-[99]"
            aria-hidden="true"
            onClick={closeTooltip}
          />
          <TooltipPopup
            date={tooltipData.date}
            revenues={revenues}
            expenses={expenses}
            position={tooltipData.position}
            onClose={closeTooltip}
          />
        </>
      )}
    </div>
  );
}

export default DualCalendar;
